import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../context/UserAuthContext';
import { FaUser, FaSave, FaTimes, FaTrashAlt, FaExclamationTriangle, FaInfoCircle, FaCheck, FaLock, FaEnvelope, FaPhone, FaGoogle } from 'react-icons/fa';
import api from '../../services/api';
import './Profile.css';

const UserProfile = () => {
  const { t } = useTranslation();
  const { user, userProfile, updateUserProfile } = useUserAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [profile, setProfile] = useState({
    displayName: '',
    phoneNumber: '',
    photoURL: ''
  });

  // Load user profile data when component mounts
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userProfile) {
      setProfile({
        displayName: userProfile.displayName || '',
        phoneNumber: userProfile.phoneNumber || '',
        photoURL: userProfile.photoURL || ''
      });
      setLoading(false);
    }
    
    // Ensure the body has the blue background class - same approach as photo gallery
    document.body.style.backgroundColor = 'var(--tertiary-color)';
    document.body.classList.add('blue-background');
    document.documentElement.classList.add('blue-background');
    
    // Cleanup function to reset body color when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
      // Don't remove classes here, RouteStyleManager will handle it
    };
  }, [user, userProfile, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      setSaving(true);
      
      // Validate inputs
      if (!profile.displayName.trim()) {
        setError(t('profile.nameRequired'));
        setSaving(false);
        return;
      }
      
      // Update profile
      await updateUserProfile({
        displayName: profile.displayName,
        phoneNumber: profile.phoneNumber
      });
      
      setSuccess(t('profile.updateSuccess'));
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(t('profile.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };
  
  const handleShowDeleteModal = () => {
    setShowDeleteModal(true);
  };
  
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };
  
  const handleDeleteAccount = async () => {
    setDeleting(true);
    setError('');
    
    try {
      // Call backend to delete user account and all associated data
      await api.delete('/users/account');
      
      // Sign out user and redirect to home page
      // This assumes the logout function is available in the useUserAuth context
      // If not, you would need to sign out the user here
      if (window.firebase && window.firebase.auth) {
        await window.firebase.auth().signOut();
      }
      
      // Show success message before redirecting
      setSuccess(t('accountDeletion.successMessage'));
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container className="profile-container d-flex justify-content-center align-items-center">
        <Spinner animation="border" variant="light" />
      </Container>
    );
  }

  return (
    <>
      <div className="profile-container">
        <Container className="text-center">
          <div className="profile-header">
            <h2>{t('profile.title')}</h2>
            <p>{t('profile.subtitle')}</p>
          </div>
        
          <Card className="profile-card" style={{ zIndex: 1010 }}>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}
              
              <div className="profile-avatar">
                {profile.photoURL ? (
                  <img 
                    src={profile.photoURL} 
                    alt={profile.displayName || t('profile.user')} 
                    className="avatar-image"
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <FaUser size={50} />
                  </div>
                )}
                <div className="avatar-info">
                  <small className="text-muted">
                    {user?.providerData?.[0]?.providerId === 'google.com' 
                      ? t('profile.googlePhotoNote')
                      : t('profile.photoComingSoon')}
                  </small>
                </div>
                <div className="account-status-badge">
                  <FaCheck size={12} />
                  <span>{t('profile.accountActive')}</span>
                </div>
              </div>
              
              <Form onSubmit={handleSubmit} className="text-start">
                {/* Personal Information Section */}
                <div className="profile-form-section">
                  <h5>
                    <FaUser className="section-icon" />
                    {t('profile.personalInfo')}
                  </h5>
                  
                  <div className="profile-form-group">
                    <Form.Label>
                      <FaUser size={14} />
                      {t('profile.displayName')}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="displayName"
                      value={profile.displayName}
                      onChange={handleInputChange}
                      placeholder={t('profile.namePlaceholder')}
                      autoComplete="name"
                      required
                    />
                    <Form.Text className="text-muted">
                      {t('profile.nameHelp')}
                    </Form.Text>
                  </div>
                  
                  <div className="profile-form-group">
                    <Form.Label>
                      <FaPhone size={14} />
                      {t('profile.phoneNumber')}
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="tel"
                        name="phoneNumber"
                        value={profile.phoneNumber}
                        onChange={handleInputChange}
                        placeholder={t('profile.phonePlaceholder')}
                        autoComplete="tel"
                        disabled={user?.providerData?.[0]?.providerId === 'phone'}
                      />
                      {user?.providerData?.[0]?.providerId === 'phone' && (
                        <div className="field-locked-indicator">
                          <FaLock />
                        </div>
                      )}
                    </div>
                    {user?.providerData?.[0]?.providerId === 'phone' && (
                      <Form.Text className="text-muted">
                        {t('profile.phoneAuthNote')}
                      </Form.Text>
                    )}
                  </div>
                </div>
                
                {/* Account Information Section */}
                <div className="profile-form-section">
                  <h5>
                    <FaInfoCircle className="section-icon" />
                    {t('profile.accountInfo')}
                  </h5>
                  
                  <div className="profile-form-group">
                    <Form.Label>
                      <FaEnvelope size={14} />
                      {t('profile.email')}
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="email"
                        value={user?.email || t('profile.noEmail')}
                        disabled
                      />
                      <div className="field-locked-indicator">
                        <FaLock />
                      </div>
                    </div>
                    <Form.Text className="text-muted">
                      {t('profile.emailNote')}
                    </Form.Text>
                  </div>
                  
                  <div className="profile-form-group">
                    <Form.Label>
                      {user?.providerData?.[0]?.providerId === 'google.com' ? (
                        <FaGoogle size={14} />
                      ) : (
                        <FaUser size={14} />
                      )}
                      {t('profile.authProvider')}
                    </Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        value={user?.providerData?.[0]?.providerId === 'google.com' 
                          ? t('profile.googleProvider')
                          : user?.providerData?.[0]?.providerId === 'phone'
                            ? t('profile.phoneProvider') 
                            : t('profile.defaultProvider')}
                        disabled
                      />
                      <div className="field-locked-indicator">
                        <FaLock />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Terms & Privacy Policy Section */}
                <div className="profile-form-section">
                  <h5>
                    <FaInfoCircle className="section-icon" />
                    {t('profile.termsPrivacy')}
                  </h5>
                  
                  <div className="terms-privacy-content">
                    <p className="text-muted small">
                      {t('profile.termsPrivacyText')}
                    </p>
                    <div className="terms-privacy-links">
                      <a 
                        href="/terms-policy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="terms-privacy-link"
                      >
                        <FaInfoCircle className="me-2" />
                        {t('profile.viewTermsPrivacy')}
                      </a>
                    </div>
                  </div>
                </div>
                
                <div className="profile-actions">
                  <Button 
                    variant="secondary" 
                    onClick={handleCancel}
                    disabled={saving}
                    className="cancel-btn"
                  >
                    <FaTimes className="me-2" />
                    {t('cancel')}
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={saving}
                    className="save-btn"
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t('saving')}
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {t('profile.saveButton')}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
              
              <hr className="my-4" />
              
              <div className="delete-account-section">
                <h4 className="text-danger">
                  <FaExclamationTriangle className="me-2" />
                  {t('accountDeletion.deleteAccount')}
                </h4>
                <p className="text-muted small">
                  {t('termsPolicy.dataDeleteText')}
                </p>
                <Button 
                  variant="outline-danger" 
                  onClick={handleShowDeleteModal}
                  className="delete-account-btn"
                >
                  <FaTrashAlt className="me-2" />
                  {t('accountDeletion.deleteAccount')}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </div>
      
      {/* Delete Account Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCancelDelete}
        centered
        backdrop="static"
        style={{ zIndex: 2000 }} /* Ensure modal appears above all content and UI elements */
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaExclamationTriangle className="me-2" />
            {t('accountDeletion.confirmTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('accountDeletion.confirmText')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete} disabled={deleting}>
            {t('accountDeletion.cancelButton')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteAccount}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('loading')}
              </>
            ) : (
              <>
                <FaTrashAlt className="me-2" />
                {t('accountDeletion.deleteButton')}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserProfile;